import { lazy, Suspense, useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { Loader2, Package } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import ViewerErrorBoundary from '@/components/labels/ViewerErrorBoundary';
import { fetchLabelBlob, preloadPdfjs } from '@/lib/labelPreload';

// Montado en la raíz de la app (fuera del Router, dentro de los providers).
// Escucha el evento `bhgc-open-label-viewer` despachado por labelView.openLabelPage
// y abre el visor de etiquetas en un modal encima de todo. Si ningún provider
// está montado, openLabelPage cae a window.open de la ruta pública .pdf.
//
// El marco del modal (Dialog + título + "Cargando etiqueta…") es ligero y se
// pinta en el mismo instante del evento (flushSync). Solo el contenido (PDF.js,
// descarga e impresión) se carga bajo demanda: antes todo el visor era un chunk
// lazy con fallback vacío que arrastraba el mapa de rastreo (~800 KB) y no se
// veía nada hasta terminar de descargarlo.
const LabelViewerContent = lazy(() => import('@/components/labels/LabelViewerContent'));

function ViewerLoading() {
  return (
    <div className="flex-1 min-h-0 flex flex-col items-center justify-center gap-3 bg-ink">
      <Loader2 className="w-8 h-8 text-cyan animate-spin" />
      <p className="text-sm text-ink-muted">Cargando etiqueta…</p>
    </div>
  );
}

export default function LabelViewerProvider() {
  const [n, setN] = useState(null);
  const [labelPromise, setLabelPromise] = useState(null);

  useEffect(() => {
    const handler = (ev) => {
      ev.preventDefault(); // señala a openLabelPage que el modal lo atendió
      const nn = ev.detail?.n || null;
      // Dispara ambas descargas en el mismo instante que se pinta el marco del
      // modal: el PDF (publicDownloadLabel) y pdf.js (~1 MB) bajan en paralelo
      // con el chunk del visor, no secuencialmente después de que este monta.
      // El .catch evita un unhandled rejection si el visor aún no se ha montado
      // para consumir la promesa; el visor sigue recibiendo el rechazo.
      const p = nn ? fetchLabelBlob(nn) : null;
      if (p) p.catch(() => {});
      flushSync(() => {
        setN(nn);
        setLabelPromise(p);
      });
      if (nn) {
        preloadPdfjs();
        // Precalienta los chunks del modal de rastreo (~800 KB con maplibre) en
        // paralelo con el PDF, para que el botón "Rastrear" del visor abra el
        // modal al instante en vez de esperar la descarga tras el clic.
        import('@/components/tracking/TrackingModal').catch(() => {});
        import('@/components/tracking/MultiTrackingModal').catch(() => {});
      }
    };
    window.addEventListener('bhgc-open-label-viewer', handler);
    return () => window.removeEventListener('bhgc-open-label-viewer', handler);
  }, []);

  // Mientras el visor está abierto el mapa de fondo (si está encendido) congela
  // sus animaciones: el modal lo cubre y así no compite por CPU/GPU con el
  // montaje del visor y el render del PDF.
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('plm-pause', { detail: !!n }));
    return () => window.dispatchEvent(new CustomEvent('plm-pause', { detail: false }));
  }, [n]);

  if (!n) return null;
  return (
    <Dialog open onOpenChange={(v) => { if (!v) { setN(null); setLabelPromise(null); } }}>
      <DialogContent className="bg-ink-surface border-ink-border p-0 gap-0 max-w-4xl w-[96vw] h-[92dvh] flex flex-col overflow-hidden">
        <DialogHeader className="p-3 sm:p-4 border-b border-ink-border space-y-0 shrink-0">
          <DialogTitle className="flex items-center gap-2 font-display font-bold text-sm uppercase tracking-wide text-ink-text pr-8">
            <Package className="w-4 h-4 text-cyan shrink-0" />
            <span className="truncate">Etiqueta · <span className="font-mono normal-case tracking-normal">{n}</span></span>
          </DialogTitle>
        </DialogHeader>
        <ViewerErrorBoundary key={n} n={n}>
          <Suspense fallback={<ViewerLoading />}>
            <LabelViewerContent n={n} prefetched={labelPromise} />
          </Suspense>
        </ViewerErrorBoundary>
      </DialogContent>
    </Dialog>
  );
}